"use client";
import dynamic from "next/dynamic";
import Section from "@/Components/LayoutComponents/Section";
const Button = dynamic(() => import("@/Components/Button"),{ssr: true});
const ServicesCRMCard = dynamic(() => import("./ServicesCrmCard"),{ssr: true});
import "swiper/css";
const MobileSlider = dynamic(() => import("./mobile-slider"),{ssr: true});
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import Link from "next/link";
const ServicesCrmCardMobile = dynamic(() => import("./ServicesCrmCardMobile"),{ssr: true});

const baseUrl = `${process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL}`;

// Loop speed in px per second
const LOOP_SPEED = 110;
// How quickly the loop eases to a stop on hover and back up to speed, in ms
const LOOP_STOP_MS = 120;
const LOOP_START_MS = 300;
// Pixels a press has to move before it counts as a drag instead of a tap / click
const DRAG_THRESHOLD = 6;
// How quickly a flick's momentum dies out, in ms
const MOMENTUM_DECAY_MS = 325;
const CARD_WIDTH = "45vh";
const CARD_OPEN_WIDTH = "105vh";

interface CRMServicesProps {
  isMobile?: boolean;
  data?: any;
}

const CRMServices: React.FC<CRMServicesProps> = ({
  data,
  isMobile,
}) => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const pointerTypeRef = useRef<string>("mouse");
  const inViewRef = useRef(false);
  const openIdRef = useRef<string | null>(null);
  // Set when the last press turned into a drag, so its click doesn't toggle a card
  const draggedRef = useRef(false);

  // Enough copies of the list to always fill the viewport while looping
  const [copies, setCopies] = useState(2);
  // Card expanded on desktop / iPad; its id is `${copy}-${index}` so only that DOM card opens
  const [openId, setOpenId] = useState<string | null>(null);
  // Card whose info is open on phones
  const [mobileOpenIndex, setMobileOpenIndex] = useState<number | null>(null);

  const services: any[] = data?.services ?? [];

  // Width of one full copy of the list, gap included
  const getSetWidth = () => {
    const first = cardRefs.current["0-0"];
    const nextCopyFirst = cardRefs.current["1-0"];
    if (!first || !nextCopyFirst) return 0;
    return nextCopyFirst.offsetLeft - first.offsetLeft;
  };

  useEffect(() => {
    if (isMobile || !services.length) return;

    const updateCopies = () => {
      if (openIdRef.current) return;
      const setWidth = getSetWidth();
      if (!setWidth) return;
      setCopies(Math.max(2, Math.ceil(window.innerWidth / setWidth) + 1));
    };
    updateCopies();
    window.addEventListener("resize", updateCopies);
    return () => window.removeEventListener("resize", updateCopies);
  }, [isMobile, services.length]);

  useEffect(() => {
    if (isMobile || !services.length || !trackRef.current || !sectionRef.current) return;
    const track = trackRef.current;
    let x = 0;
    let speed = LOOP_SPEED;
    // Momentum left over from a drag / flick, in px per second
    let velocity = 0;
    let drag: {
      pointerId: number;
      startX: number;
      startTrackX: number;
      lastX: number;
      lastTime: number;
      active: boolean;
    } | null = null;
    // Cached so the ticker never reads layout; re-measured only when the track resizes
    let setWidth = getSetWidth();
    const resizeObserver = new ResizeObserver(() => {
      setWidth = getSetWidth();
    });
    resizeObserver.observe(track);

    gsap.set(track, { force3D: true });
    const setX = gsap.quickSetter(track, "x", "px");

    const observer = new IntersectionObserver(([entry]) => {
      inViewRef.current = entry.isIntersecting;
    });
    observer.observe(sectionRef.current);

    // Keep x inside one copy's width, in both directions, so the loop never runs out of cards
    const render = () => {
      if (!setWidth) return;
      while (x <= -setWidth) x += setWidth;
      while (x > 0) x -= setWidth;
      setX(x);
    };

    const tick = (_time: number, deltaTime: number) => {
      if (!inViewRef.current || !setWidth || drag?.active) return;
      // Ease the speed instead of stopping / starting abruptly
      const target = openIdRef.current ? 0 : LOOP_SPEED;
      const easeMs = target ? LOOP_START_MS : LOOP_STOP_MS;
      speed += (target - speed) * (1 - Math.exp(-deltaTime / easeMs));
      if (!target && speed < 8) speed = 0;
      if (openIdRef.current) velocity = 0;
      velocity *= Math.exp(-deltaTime / MOMENTUM_DECAY_MS);
      if (Math.abs(velocity) < 5) velocity = 0;
      if (!speed && !velocity) return;
      x += ((velocity - speed) * deltaTime) / 1000;
      render();
    };
    gsap.ticker.add(tick);

    // Drag (mouse) / swipe (touch) to move through the cards by hand
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      draggedRef.current = false;
      // Pressing catches a flick that is still coasting
      velocity = 0;
      drag = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startTrackX: x,
        lastX: e.clientX,
        lastTime: e.timeStamp,
        active: false,
      };
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      const dx = e.clientX - drag.startX;
      if (!drag.active) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        drag.active = true;
        draggedRef.current = true;
        velocity = 0;
        speed = 0;
        // Capturing makes the hovered card see a pointerleave, so it closes while dragging
        track.setPointerCapture(e.pointerId);
        setOpenId(null);
      }
      const dt = e.timeStamp - drag.lastTime;
      if (dt > 0) {
        // Smoothed so one uneven event doesn't decide the flick speed
        velocity = velocity * 0.2 + ((e.clientX - drag.lastX) / dt) * 1000 * 0.8;
      }
      drag.lastX = e.clientX;
      drag.lastTime = e.timeStamp;
      x = drag.startTrackX + dx;
      render();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (!drag || e.pointerId !== drag.pointerId) return;
      // A pause before letting go means no flick
      const released = drag.active && e.type === "pointerup" && e.timeStamp - drag.lastTime < 80;
      if (!released) velocity = 0;
      if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
      drag = null;
    };

    // Trackpad sideways scroll moves the cards too
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      x -= e.deltaMode === 1 ? e.deltaX * 16 : e.deltaX;
      speed = 0;
      velocity = 0;
      render();
    };

    // Stop the browser's native image drag from hijacking the mouse drag
    const onDragStart = (e: DragEvent) => e.preventDefault();

    track.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    track.addEventListener("wheel", onWheel, { passive: false });
    track.addEventListener("dragstart", onDragStart);

    return () => {
      gsap.ticker.remove(tick);
      track.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      track.removeEventListener("wheel", onWheel);
      track.removeEventListener("dragstart", onDragStart);
      observer.disconnect();
      resizeObserver.disconnect();
      gsap.set(track, { x: 0 });
    };
  }, [isMobile, services.length]);

  // Expand the open card, collapse the one that was open before
  useEffect(() => {
    const prevId = openIdRef.current;
    openIdRef.current = openId;
    if (prevId === openId) return;
    [prevId, openId].forEach((id) => {
      const node = id ? cardRefs.current[id] : null;
      if (!node) return;
      gsap.to(node, {
        width: id === openId ? CARD_OPEN_WIDTH : CARD_WIDTH,
        duration: 0.6,
        ease: "power2.inOut",
        overwrite: "auto",
      });
    });
  }, [openId]);

  // Touch: tapping outside the cards closes the open one and resumes the loop
  useEffect(() => {
    if (!openId) return;
    const handleOutside = (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      if (trackRef.current?.contains(e.target as Node)) return;
      setOpenId(null);
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [openId]);

  return (
    <Section className="xl:py-[100px] md:py-[95px] py-[56px] relative bg-[url(/images/bg-block.png)] bg-size-[100%]  bg-top sm:bg-[url(/images/enterprise-bg.png)] sm:bg-size-[60%] bg-no-repeat sm:bg-left-top">
      <div className="absolute z-[-1] top-0 right-0 w-full h-full bg-[linear-gradient(96.18deg,_#E6F5FF_0%,_#5AA7DF_100%)]">
        <div className="w-full h-full bg-[linear-gradient(0deg,_rgba(255,_255,_255,_0.8),_rgba(255,_255,_255,_0.8))]" />
      </div>

      {!isMobile && (
        <div className="overflow-hidden">
          <div className="container text-center">
            <h2 className="text-heading-1 max-w-[1020px] mx-auto font-bold text-primary-text mb-[24px]">
              {data?.title}
            </h2>

            {data?.cta?.href && data?.cta?.label && (
              <Link href={data?.cta?.href}>
                <div className="max-auto text-center flex items-center justify-center mb-[40px]">
                  <Button
                    type="button"
                    label={data?.cta?.label}
                    className="text-subtitle-4"
                  />
                </div>
              </Link>
            )}
          </div>

          <div ref={sectionRef} className="relative z-[5] py-[40px]">
            <div
              ref={trackRef}
              // pan-y keeps vertical page scrolling on touch while horizontal swipes move the cards
              className="flex gap-[35px] items-center w-max will-change-transform touch-pan-y select-none cursor-grab active:cursor-grabbing"
            >
              {Array.from({ length: copies }).map((_, copy) =>
                services.map((service: any, i: number) => {
                  const id = `${copy}-${i}`;
                  return (
                    <div
                      key={`${service.id ?? i}-${copy}`}
                      ref={(el) => {
                        cardRefs.current[id] = el;
                      }}
                      aria-hidden={copy > 0 ? true : undefined}
                      className="flex-shrink-0 overflow-hidden bg-white flex items-center justify-start text-lg font-semibold"
                      style={{
                        width: CARD_WIDTH,
                        height: "70vh",
                      }}
                      // Laptop / PC: hover stops the loop and shows the details
                      onPointerEnter={(e) => {
                        if (e.pointerType === "mouse") setOpenId(id);
                      }}
                      onPointerLeave={(e) => {
                        if (e.pointerType === "mouse") setOpenId(null);
                      }}
                      // iPad / touch: tap toggles the details and the loop
                      onPointerDown={(e) => {
                        pointerTypeRef.current = e.pointerType;
                      }}
                      onClick={() => {
                        if (pointerTypeRef.current === "mouse" || draggedRef.current) return;
                        setOpenId((current) => (current === id ? null : id));
                      }}
                    >
                      <ServicesCRMCard
                        backgroundImage={
                          service?.image?.data?.attributes?.url
                            ? `${baseUrl}${service.image.data.attributes.url}`
                            : ""
                        }
                        title={service?.title}
                        description={service?.description}
                        isOpen={openId === id}
                      />
                    </div>
                  );
                }),
              )}
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div className="container ">
          <div className="text-center">
            <h2 className="text-heading-1 max-w-[1020px] mx-auto font-bold text-primary-text mb-[24px]">
              {data?.title}
            </h2>

            {data?.cta?.href && data?.cta?.label && (
              <Link href={data?.cta?.href}>
                <div className="max-auto text-center flex items-center justify-center mb-[40px]">
                  <Button
                    type="button"
                    label={data?.cta?.label}
                    className="text-subtitle-4"
                  />
                </div>
              </Link>
            )}
          </div>
          <>
            <MobileSlider
              paused={mobileOpenIndex !== null}
              onSlideChanged={() => setMobileOpenIndex(null)}
            >
              {services.map((services: any, i: number) => (
                <div key={services.id}>
                  <div className="group h-full w-full rounded-2xl shadow-md transition-transform duration-300 ease-in-out group-hover:scale-105 group-hover:shadow-xl">
                    <ServicesCrmCardMobile
                      backgroundImage={
                        services?.image?.data?.attributes?.url
                          ? `${baseUrl}${services.image.data.attributes.url}`
                          : ""
                      }
                      title={services?.title}
                      description={services?.description}
                      isOpen={mobileOpenIndex === i}
                      onToggle={() =>
                        setMobileOpenIndex((current) => (current === i ? null : i))
                      }
                    />
                  </div>
                </div>
              ))}
            </MobileSlider>
          </>
        </div>
      )}
    </Section>
  );
};
export default CRMServices;
